import { ThumbnailLinkCell } from 'components/table/cells/ThumbnailCell'
import { AssetViz } from 'features/assetViz'
import { useRolodexEntries } from 'features/rolodex'
import React from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoComponentDataFrame,
  getSortedAssetModels,
  RolodexEntryType,
} from 'types'

const PlaceholderThumbnail = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-radius: 4px;
`

const StyledThumbnail = styled.div`
  width: 40px;
  height: 40px;
`

const Thumbnail: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return children ? (
    <StyledThumbnail>{children}</StyledThumbnail>
  ) : (
    <StyledThumbnail>
      <PlaceholderThumbnail />
    </StyledThumbnail>
  )
}

export const InspectionThumbnail: React.FC<{
  inspectionID: string
  unitID: string
  linkable: boolean
}> = ({ inspectionID: inspectionId, unitID, linkable }) => {
  const { theme } = useGeckoTheme()
  const {
    inspections,
    componentDataFrames,
    componentDatasets,
    componentDefaults,
  } = useRolodexEntries({
    type: [
      RolodexEntryType.INSPECTION,
      RolodexEntryType.COMPONENT_DEFAULTS,
      RolodexEntryType.COMPONENT_DATA_FRAME,
      RolodexEntryType.COMPONENT_DATASET,
      RolodexEntryType.ASSET_MODEL,
      RolodexEntryType.COMPONENT,
    ],
    tags: { inspection: inspectionId },
  })

  const { components } = useRolodexEntries({
    ids: [inspections[0]?.tags.component].filter((id): id is string => !!id),
  })

  const { assetModels } = useRolodexEntries({
    type: RolodexEntryType.ASSET_MODEL,
    tags: { unit: unitID ? [unitID] : [] },
  })

  const sortedCDFs = [...componentDataFrames].sort((a, b) => {
    return GeckoComponentDataFrame.DefaultSort(a, b, componentDatasets)
  })

  const selectedCDF = sortedCDFs.at(0) ?? undefined

  const colorThreshold =
    componentDefaults[0] && selectedCDF
      ? componentDefaults[0].data.color_scale_thresholds.get(
          selectedCDF.data.params.view.id
        )
      : undefined
  const latestAssetModel = getSortedAssetModels([...assetModels])[0]
  return (
    <ThumbnailLinkCell
      thumbnailString={
        inspections[0] ? inspections[0].data.date.toDateString() : '-'
      }
      thumbnailSubtitle={components[0] && components[0].data.name}
      to='/details/$entryID'
      params={{ entryID: inspectionId }}
      hoverable={linkable}>
      <Thumbnail>
        <AssetViz
          assetModel={latestAssetModel}
          components={components ? components : []}
          assetColor={theme.forecast.secondaryText}
          getColorThresholdForComponent={() => colorThreshold}
          componentDataFrames={selectedCDF ? [selectedCDF] : []}
          loadingView={false}
        />
      </Thumbnail>
    </ThumbnailLinkCell>
  )
}
